/**
 * Collection Primitive Operations
 *
 * Operations for working with arrays and sequences.
 */

import { defineOperation, PrimitiveOperation } from './operations.model';

// ------------------------------------------------
// AGGREGATION OPERATIONS
// ------------------------------------------------

export const sum = defineOperation<number[], number>({
  id: 'op.sum',
  name: 'Sum',
  symbol: 'Σ',
  category: 'collection',
  arity: 1,
  execute: (arr: number[]) => arr.reduce((acc: number, val: number) => acc + val, 0),
  latex: '\\sum {{arr}}',
  description: 'Adds all elements of an array',
  example: '$\\sum [1,2,3,4] = 10$',
});

export const product = defineOperation<number[], number>({
  id: 'op.product',
  name: 'Product',
  symbol: 'Π',
  category: 'collection',
  arity: 1,
  execute: (arr: number[]) => arr.reduce((acc: number, val: number) => acc * val, 1),
  latex: '\\prod {{arr}}',
  description: 'Multiplies all elements of an array',
  example: '$\\prod [1,2,3,4] = 24$',
});

export const min = defineOperation<number[], number>({
  id: 'op.min',
  name: 'Minimum',
  symbol: 'min',
  category: 'collection',
  arity: 1,
  execute: (arr) => Math.min(...arr),
  latex: '\\min({{arr}})',
  description: 'Returns the smallest element in an array',
  example: '$\\min([3,1,4,1,5]) = 1$',
});

export const max = defineOperation<number[], number>({
  id: 'op.max',
  name: 'Maximum',
  symbol: 'max',
  category: 'collection',
  arity: 1,
  execute: (arr) => Math.max(...arr),
  latex: '\\max({{arr}})',
  description: 'Returns the largest element in an array',
  example: '$\\max([3,1,4,1,5]) = 5$',
});

// ------------------------------------------------
// ACCESS OPERATIONS
// ------------------------------------------------

export const nth = defineOperation<unknown, unknown>({
  id: 'op.nth',
  name: 'N-th Element',
  symbol: 'aₙ',
  category: 'collection',
  arity: 2,
  execute: (arr: unknown[], n: number) => arr[n],
  latex: '{{arr}}_{{{n}}}',
  description: 'Gets the element at index n (0-based)',
  example: '$[a,b,c]_1 = b$',
});

export const first = defineOperation<unknown[], unknown>({
  id: 'op.first',
  name: 'First Element',
  symbol: 'a₀',
  category: 'collection',
  arity: 1,
  execute: (arr) => arr[0],
  latex: '{{arr}}_0',
  description: 'Gets the first element of an array',
  example: '$[5,3,7]_0 = 5$',
});

export const last = defineOperation<unknown[], unknown>({
  id: 'op.last',
  name: 'Last Element',
  symbol: 'aₙ',
  category: 'collection',
  arity: 1,
  execute: (arr) => arr[arr.length - 1],
  latex: '{{arr}}_{n}',
  description: 'Gets the last element of an array',
  example: '$[5,3,7]_n = 7$',
});

export const length = defineOperation<unknown[], number>({
  id: 'op.length',
  name: 'Length',
  symbol: '|a|',
  category: 'collection',
  arity: 1,
  execute: (arr) => arr.length,
  latex: '|{{arr}}|',
  description: 'Returns the number of elements in an array',
  example: '$|[1,2,3,4,5]| = 5$',
});

// ------------------------------------------------
// GENERATION OPERATIONS
// ------------------------------------------------

export const range = defineOperation<number, number[]>({
  id: 'op.range',
  name: 'Range',
  symbol: '[1..n]',
  category: 'collection',
  arity: 2,
  execute: (start: number, end: number) => {
    const result: number[] = [];
    for (let i = start; i <= end; i++) {
      result.push(i);
    }
    return result;
  },
  latex: '[{{start}}, \\ldots, {{end}}]',
  description: 'Generates a sequence of integers from start to end',
  example: '$[1, \\ldots, 5] = [1,2,3,4,5]$',
});

export const repeat = defineOperation<unknown, unknown[]>({
  id: 'op.repeat',
  name: 'Repeat',
  symbol: 'repeat',
  category: 'collection',
  arity: 2,
  execute: (value: unknown, count: number) => Array(count).fill(value),
  latex: '\\text{repeat}({{value}}, {{count}})',
  description: 'Creates an array with a value repeated n times',
  example: '$\\text{repeat}(0, 3) = [0,0,0]$',
});

// ------------------------------------------------
// TRANSFORMATION OPERATIONS
// ------------------------------------------------

export const concat = defineOperation<unknown[], unknown[]>({
  id: 'op.concat',
  name: 'Concatenate',
  symbol: '++',
  category: 'collection',
  arity: 2,
  execute: (a: unknown[], b: unknown[]) => [...a, ...b],
  latex: '{{a}} \\mathbin{+\\mkern-10mu+} {{b}}',
  description: 'Joins two arrays together',
  example: '$[1,2] \\mathbin{+\\mkern-10mu+} [3,4] = [1,2,3,4]$',
});

export const reverse = defineOperation<unknown[], unknown[]>({
  id: 'op.reverse',
  name: 'Reverse',
  symbol: 'rev',
  category: 'collection',
  arity: 1,
  execute: (arr) => [...arr].reverse(),
  latex: '\\text{rev}({{arr}})',
  description: 'Reverses the order of elements in an array',
  example: '$\\text{rev}([1,2,3]) = [3,2,1]$',
});

export const slice = defineOperation<unknown, unknown[]>({
  id: 'op.slice',
  name: 'Slice',
  symbol: 'slice',
  category: 'collection',
  arity: 3,
  execute: (arr: unknown[], start: number, end: number) => arr.slice(start, end),
  latex: '{{arr}}[{{start}}:{{end}}]',
  description: 'Extracts a portion of an array from start to end index',
  example: '$[1,2,3,4,5][1:4] = [2,3,4]$',
});

// ------------------------------------------------
// EXPORT ALL COLLECTION OPERATIONS
// ------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const collectionOperations: Array<PrimitiveOperation<any, any>> = [
  sum,
  product,
  min,
  max,
  nth,
  first,
  last,
  length,
  range,
  repeat,
  concat,
  reverse,
  slice,
];
